import { toast } from "sonner";

// True when the app is running inside an iframe (e.g. the embedded preview pane),
// where camera/mic are blocked by Permissions-Policy unless the iframe opts in.
export const isEmbedded = () => {
  try {
    return window.self !== window.top;
  } catch (_) {
    return true; // cross-origin access throws → we're embedded
  }
};

// Wrapper around getUserMedia that classifies failures into a friendly, actionable
// message and attaches `.friendly` + `.embedded` to the thrown error.
export async function requestMedia(constraints) {
  if (!window.isSecureContext) {
    const e = new Error("insecure");
    e.friendly = "Camera/mic need a secure (https) connection.";
    throw e;
  }
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    const e = new Error("unsupported");
    e.friendly = "This browser can't access the camera/mic. Try the latest Chrome, Edge or Safari.";
    throw e;
  }
  try {
    return await navigator.mediaDevices.getUserMedia(constraints);
  } catch (err) {
    const embedded = isEmbedded();
    if (err.name === "NotAllowedError" || err.name === "SecurityError") {
      err.embedded = embedded;
      err.friendly = embedded
        ? "Camera/mic are blocked inside the embedded preview window. Open the app in its own browser tab (or the installed Messenger app) to use video/voice."
        : "Camera/mic access was blocked. Click the camera icon in your browser's address bar, choose Allow, then try again.";
    } else if (err.name === "NotFoundError" || err.name === "OverconstrainedError") {
      err.friendly = "No camera/microphone was found on this device.";
    } else if (err.name === "NotReadableError") {
      err.friendly = "Your camera/mic is already in use by another app. Close it and try again.";
    } else {
      err.friendly = "Couldn't access your camera/mic.";
    }
    throw err;
  }
}

// Show a friendly error toast; in embedded mode, offer an "Open in new tab" action.
export function toastMediaError(err) {
  const msg = err?.friendly || "Couldn't access your camera/mic.";
  if (err?.embedded) {
    toast.error(msg, {
      action: {
        label: "Open in new tab",
        onClick: () => window.open(window.location.href, "_blank", "noopener"),
      },
      duration: 8000,
    });
  } else {
    toast.error(msg);
  }
}
